import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingBasket, Truck, Calendar, Sun, Leaf, ShieldCheck, Sprout, Utensils, ShoppingBasket as Basket, Store, GlassWater, Hotel, PartyPopper } from 'lucide-react';
import { BRAND, BOXES, SEGMENTS, STOCK } from '@/lib/constants';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import SquareButton from '@/components/SquareButton';
import SeasonalCalendar from '@/components/SeasonalCalendar';
import Seo from '@/components/Seo';
import { BoxOrderDialog } from '@/components/forms/BoxOrderDialog';

const SEGMENT_ICONS = { Utensils, ShoppingBasket: Basket, Store, GlassWater, Hotel, PartyPopper, Truck };
const VARIETY_CHIPS = [
  { name: 'Tommy Atkins', color: 'bg-[hsl(var(--c-mango-red))]', image: '/media/p5.jpg' },
  { name: 'Haden', color: 'bg-[hsl(var(--c-mango-orange))]', image: '/media/p6.jpg' },
  { name: 'Keitt', color: 'bg-[hsl(var(--c-leaf))]', image: '/media/p4.jpg' },
  { name: 'Kent', color: 'bg-[hsl(var(--c-mango-yellow))]', image: '/media/p6.jpg' },
  { name: 'Valencia Pride', color: 'bg-[hsl(var(--c-mango-orange))]', image: '/media/p5.jpg' },
  { name: 'Carrie', color: 'bg-[hsl(var(--c-leaf))]', image: '/media/p4.jpg' },
  { name: "Grower's Mix", color: 'bg-[hsl(var(--c-teal))]', image: '/media/p7.jpg' },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Premium Florida-Grown Mangoes Direct From Local Growers"
        description="Florida SunKissed Mangoes connects South Florida’s sun-soaked groves with families, restaurants, retailers, and distributors across America. Tree-ripened, hand-selected, harvested at peak flavor."
        keywords="Florida mangoes, Florida-grown mangoes, mango boxes, wholesale mangoes, tree-ripened mangoes, South Florida mangoes, mango distribution, Coastal Capital AgriCo"
      />

      <Hero
        testId="home-hero-section"
        eyebrow="Florida-Grown • Tree-Ripened • Locally Sourced"
        title={<>From South Florida groves to <span className="text-[hsl(var(--c-mango-red))]">your kitchen</span>.</>}
        subtitle="Florida SunKissed Mangoes connects South Florida’s sun-soaked groves with families, restaurants, retailers, and distributors across America. Hand-selected, tree-ripened, harvested at peak flavor."
        block="cream"
        bgImage={STOCK.mango_closeup}
        bgVideo={STOCK.video_hero}
        bgPoster={STOCK.video_hero_poster}
        rightImage={STOCK.mango_closeup}
        imageBlock="orange"
        rightAccent="yellow"
        leftAccent="red"
        ctas={[
          { label: 'Shop Mango Boxes', to: '/shop', variant: 'orange', testId: 'home-hero-shop-button', icon: <ShoppingBasket className="h-4 w-4" /> },
          { label: 'Wholesale & Bulk', to: '/wholesale', variant: 'teal', testId: 'home-hero-wholesale-button', icon: <Truck className="h-4 w-4" /> },
          { label: 'Local Delivery', to: '/shop#order-options', variant: 'ink', testId: 'home-hero-delivery-button', icon: <Calendar className="h-4 w-4" /> },
        ]}
      />

      {/* Marquee */}
      <section aria-hidden="true" className="bg-[hsl(var(--c-mango-yellow))] border-b-2 border-[hsl(var(--c-ink))] overflow-hidden">
        <div className="flex whitespace-nowrap py-4 animate-marquee text-[hsl(var(--c-ink))] font-heading text-2xl sm:text-3xl font-extrabold tracking-[-0.01em]">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex items-center shrink-0">
              {['Tommy Atkins', 'Haden', 'Keitt', 'Kent', 'Valencia Pride', 'Carrie', "Grower's Choice"].map((v, i) => (
                <span key={`${idx}-${i}`} className="flex items-center px-8">
                  <span>{v}</span>
                  <span className="mx-8 inline-block h-3 w-3 bg-[hsl(var(--c-mango-red))]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* WHY FLORIDA */}
      <section data-testid="home-why-florida-section" className="block-cream">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              eyebrow="Why Florida"
              size="xl"
              title={<>Real fruit. <br className="hidden sm:block" /> Real growers. Real Florida.</>}
              subtitle="We don’t source from import warehouses. We source from a network of South Florida homeowners, family farms, and grove partners we know by name."
            />
          </Reveal>
          <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {[
              { icon: Sun, accent: 'bg-[hsl(var(--c-mango-yellow))]', title: 'Tree-ripened sweetness', body: 'We harvest at the peak of ripeness, so every box delivers full Florida flavor — not picked-green imports.' },
              { icon: Leaf, accent: 'bg-[hsl(var(--c-leaf))]', title: 'Direct from local growers', body: 'Our growers are Palm Beach, Broward, and Miami-Dade residents. You’re supporting Florida’s backyard ag economy.' },
              { icon: ShieldCheck, accent: 'bg-[hsl(var(--c-mango-red))]', title: 'Hand-selected & quality checked', body: 'Every mango is hand-inspected, packed with care, and matched with a ripening guide for the variety inside.' },
            ].map(({ icon: Icon, title, body, accent }, i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className={`bg-white p-7 sm:p-8 lg:p-10 h-full lift-on-hover ${i < 2 ? 'md:border-r-2 md:border-[hsl(var(--c-ink))]' : ''} ${i < 2 ? 'border-b-2 md:border-b-0 border-[hsl(var(--c-ink))]' : ''}`}>
                  <div className={`inline-flex h-14 w-14 items-center justify-center ${accent} border-2 border-[hsl(var(--c-ink))]`}>
                    <Icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 sm:mt-6 font-heading text-2xl lg:text-3xl font-extrabold leading-tight">{title}</h3>
                  <p className="mt-3 text-[hsl(var(--c-ink-soft))] leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE PHOTO STRIP — editorial breather between sections */}
      <section data-testid="home-feature-strip" aria-hidden="true" className="relative h-[260px] sm:h-[340px] lg:h-[420px] border-t-2 border-b-2 border-[hsl(var(--c-ink))] overflow-hidden">
        <img
          src={STOCK.mangoes_pile}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--c-ink))]/45 via-transparent to-[hsl(var(--c-ink))]/35" />
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1400px] h-full px-5 sm:px-10 lg:px-16 flex items-center">
          <div className="max-w-2xl">
            <div className="eyebrow text-white/95 drop-shadow-[0_1px_0_rgba(0,0,0,0.4)]">From Grove to Table</div>
            <h3 className="mt-3 font-heading display-md font-extrabold text-white leading-[0.98] drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]">
              Hand-picked at <span className="text-[hsl(var(--c-mango-yellow))]">peak ripeness</span>, packed the same day.
            </h3>
          </div>
        </div>
        <div className="absolute top-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-yellow))]" />
        <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-orange))]" />
      </section>

      {/* SEASONAL CALENDAR */}
      <section data-testid="home-seasonal-calendar" className="block-yellow border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <SectionHeader
                eyebrow="When To Mango"
                size="xl"
                title={<>When are Florida <br className="hidden sm:block" /> mangoes in season?</>}
                subtitle="South Florida’s mango calendar runs roughly May through October. Hover or tap a variety to see its peak months."
              />
              <SquareButton to="/varieties" variant="ink" size="lg" testId="home-calendar-see-varieties">
                Explore Varieties <ArrowRight className="h-4 w-4 ml-2" />
              </SquareButton>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-10 sm:mt-12">
              <SeasonalCalendar tone="ink" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOX PROGRAM */}
      <section data-testid="home-box-program-preview" className="block-orange border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <SectionHeader
                tone="cream"
                eyebrow="The Box Program"
                size="xl"
                title={<>Pick your Florida <br className="hidden sm:block" /> mango box.</>}
                subtitle="Three sizes, hand-selected mangoes, mixed seasonal varieties when available. Each box ships with a ripening guide."
              />
              <SquareButton to="/shop" variant="ink" size="lg" testId="home-shop-see-all">
                See All Boxes <ArrowRight className="h-4 w-4 ml-2" />
              </SquareButton>
            </div>
          </Reveal>

          <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {BOXES.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.05} variant="up">
                <div className={`relative bg-white text-[hsl(var(--c-ink))] h-full flex flex-col ${i < 2 ? 'md:border-r-2 md:border-[hsl(var(--c-ink))]' : ''} ${i < 2 ? 'border-b-2 md:border-b-0 border-[hsl(var(--c-ink))]' : ''} lift-on-hover`} data-testid={`home-box-card-${b.slug}`}>
                  {b.popular && (
                    <div className="absolute top-3 right-3 z-10 bg-[hsl(var(--c-mango-red))] text-white text-[10px] tracking-[0.18em] uppercase px-3 py-2 font-bold border-2 border-[hsl(var(--c-ink))] shadow-[3px_3px_0_0_hsl(var(--c-ink))]">Most Popular</div>
                  )}
                  {/* Box image header */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden border-b-2 border-[hsl(var(--c-ink))]">
                    <img
                      src={b.image}
                      alt={`${b.name} — fresh Florida mangoes`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent" />
                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 bg-white border-2 border-[hsl(var(--c-ink))] px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold">
                      {b.weight} • {b.count}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-7 sm:p-8 lg:p-10 flex flex-col flex-1">
                    <h3 className="font-heading text-3xl font-extrabold leading-tight">{b.name}</h3>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="font-heading text-5xl sm:text-6xl text-[hsl(var(--c-mango-red))] font-extrabold">${b.price}</span>
                      <span className="text-xs text-[hsl(var(--c-ink-soft))] uppercase tracking-wider">/ box</span>
                    </div>
                    <p className="mt-4 text-[hsl(var(--c-ink-soft))]">{b.blurb}</p>
                    <ul className="mt-5 space-y-2 flex-1">
                      {b.bestFor.map((x) => (
                        <li key={x} className="flex items-start gap-2 text-sm">
                          <Sprout className="h-4 w-4 mt-0.5 text-[hsl(var(--c-leaf))] shrink-0" aria-hidden="true" /> {x}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-7">
                      <BoxOrderDialog
                        defaultBox={b.slug}
                        trigger={
                          <SquareButton variant="orange" size="lg" fullWidth testId={`home-box-order-${b.slug}`}>
                            Order Now
                          </SquareButton>
                        }
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section data-testid="home-who-we-serve-teaser" className="block-ink border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="Who We Serve"
              size="xl"
              title={<>Wholesale mangoes <br className="hidden sm:block" /> for the businesses <br className="hidden sm:block" /> that move Florida.</>}
              subtitle="From neighborhood juice bars to national produce distributors, we supply premium Florida-grown mangoes in the quantities your business needs."
            />
          </Reveal>

          <div className="mt-12 sm:mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 border-2 border-[hsl(var(--c-cream))]/30">
            {SEGMENTS.map((s, i) => {
              const Icon = SEGMENT_ICONS[s.icon] || Truck;
              const accents = ['bg-[hsl(var(--c-mango-yellow))]', 'bg-[hsl(var(--c-mango-orange))]', 'bg-[hsl(var(--c-mango-red))]', 'bg-[hsl(var(--c-leaf))]', 'bg-[hsl(var(--c-teal))]', 'bg-[hsl(var(--c-mango-yellow))]', 'bg-[hsl(var(--c-mango-orange))]'];
              return (
                <Reveal key={s.id} delay={i * 0.04}>
                  <div className="group relative bg-[hsl(var(--c-ink))] p-6 lg:p-8 h-full border-r-2 border-b-2 border-[hsl(var(--c-cream))]/30 lift-on-hover overflow-hidden" data-testid={`home-segment-${s.id}`}>
                    {/* Image tinted with ink */}
                    {s.image && (
                      <>
                        <img
                          src={s.image}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover opacity-25 transition-all duration-700 ease-out group-hover:opacity-45 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--c-ink))]/80 via-[hsl(var(--c-ink))]/60 to-[hsl(var(--c-ink))]/85" />
                      </>
                    )}
                    <div className="relative">
                      <div className={`inline-flex h-12 w-12 items-center justify-center ${accents[i]} border-2 border-[hsl(var(--c-cream))]/40 text-[hsl(var(--c-ink))]`}>
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div className="mt-5 font-heading text-xl font-extrabold leading-tight text-[hsl(var(--c-cream))]">{s.title}</div>
                      <div className="mt-2 text-sm text-[hsl(var(--c-cream))]/80 leading-relaxed">{s.blurb}</div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
            {/* Filler block with CTA so the grid completes nicely (3-col / 4-col) */}
            <Reveal>
              <Link to="/wholesale" data-testid="home-segment-cta" className="group relative h-full p-6 lg:p-8 bg-[hsl(var(--c-mango-orange))] border-r-2 border-b-2 border-[hsl(var(--c-cream))]/30 flex flex-col justify-between btn-transition hover:bg-[hsl(var(--c-mango-orange-deep))]">
              <div>
                <div className="eyebrow text-white/90">More</div>
                <div className="mt-3 font-heading text-2xl font-extrabold leading-tight text-white">Don’t see your business?</div>
                <div className="mt-2 text-sm text-white/85">We work with all kinds of buyers. Tell us what you need.</div>
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-white font-bold uppercase tracking-wider text-xs">
                Explore Wholesale <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GROWERS SPLIT */}
      <section data-testid="home-growers-teaser" className="block-leaf border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-5 sm:px-10 lg:px-16 py-20 lg:py-28 lg:border-r-2 lg:border-[hsl(var(--c-ink))]">
            <Reveal>
              <div className="eyebrow text-white/85">For Growers</div>
              <h2 className="mt-3 sm:mt-4 font-heading display-lg font-extrabold leading-[0.95] text-white tracking-[-0.02em]">
                Have mango trees?<br />
                <span className="text-[hsl(var(--c-mango-yellow))]">Let’s harvest them together.</span>
              </h2>
              <p className="mt-5 text-white/90 leading-relaxed max-w-xl text-lg">
                We work with homeowners, property managers, and growers across South Florida — buying fruit, helping with harvests, and reducing seasonal waste.
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
                {['Harvest assistance', 'Fruit purchasing', 'Tree maintenance referrals', 'Long-term grower partnerships'].map((s) => (
                  <li key={s} className="flex items-center gap-3 text-white">
                    <span className="h-2 w-2 bg-[hsl(var(--c-mango-yellow))]" /> <span className="text-sm font-semibold">{s}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <SquareButton to="/growers" variant="yellow" size="lg" testId="home-grower-cta">
                  Become a Grower Partner
                </SquareButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative bg-[hsl(var(--c-mango-yellow))] min-h-[360px] lg:min-h-0 overflow-hidden">
            <div className="absolute inset-0" style={{ backgroundImage: `url(${STOCK.mango_tree})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-0 bg-[hsl(var(--c-mango-yellow))]/45" />
            <div className="absolute inset-0 grain" />
            <div className="absolute top-0 left-0 right-0 h-3 bg-[hsl(var(--c-mango-red))]" />
            <div className="absolute bottom-0 left-0 right-0 h-3 bg-[hsl(var(--c-mango-orange))]" />
          </div>
        </div>
      </section>

      {/* VARIETIES TEASER */}
      <section className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <SectionHeader
                eyebrow="Mango Varieties"
                size="xl"
                title={<>Florida grows the <br className="hidden sm:block" /> most flavorful mangoes <br className="hidden sm:block" /> in America.</>}
                subtitle="Tommy Atkins, Haden, Keitt, Kent, Valencia Pride, Carrie — each with its own flavor, ripening, and harvest window."
              />
              <SquareButton to="/varieties" variant="leaf" size="lg" testId="home-varieties-see-all">
                Explore Varieties <ArrowRight className="h-4 w-4 ml-2" />
              </SquareButton>
            </div>
          </Reveal>
          <Reveal>
            <div className="mt-12 sm:mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-0 border-2 border-[hsl(var(--c-ink))]">
              {VARIETY_CHIPS.map((v) => (
                <div key={v.name} className="group relative bg-white text-center border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 lift-on-hover overflow-hidden" data-testid={`home-variety-chip-${v.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <div className="relative aspect-square w-full overflow-hidden border-b-2 border-[hsl(var(--c-ink))]">
                    <img
                      src={v.image}
                      alt={`${v.name} mango`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                    <div className={`absolute top-2 left-2 h-3 w-3 ${v.color} border border-[hsl(var(--c-ink))]`} aria-hidden="true" />
                  </div>
                  <div className="px-2 py-3 sm:py-4 text-[11px] sm:text-xs font-bold uppercase tracking-wider">{v.name}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR STORY BIG CTA */}
      <section data-testid="home-about-snippet" className="block-red border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-5 sm:px-10 lg:px-16 py-20 lg:py-28 lg:border-r-2 lg:border-[hsl(var(--c-ink))]">
            <div className="eyebrow text-white/85">Our Story</div>
            <h2 className="mt-3 sm:mt-4 font-heading display-lg font-extrabold leading-[0.95] text-white tracking-[-0.02em]">
              A produce sourcing & distribution venture of <span className="text-[hsl(var(--c-mango-yellow))]">{BRAND.parentLine.replace('A ', '')}</span>.
            </h2>
            <p className="mt-5 text-white/90 leading-relaxed max-w-2xl text-lg">
              We exist to connect South Florida’s abundant local harvests with the consumers, retailers, restaurants, juice bars, hotels, distributors, and event vendors who want the very best Florida-grown fruit.
            </p>
          </div>
          <div className="lg:col-span-5 relative bg-[hsl(var(--c-ink))] min-h-[360px] lg:min-h-0 overflow-hidden flex">
            {/* Photo background */}
            <img
              src={STOCK.golden_orchard}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--c-ink))]/70 via-[hsl(var(--c-ink))]/40 to-[hsl(var(--c-ink))]/80" />
            <div className="relative p-8 sm:p-10 lg:p-12 flex flex-col justify-center gap-0 w-full">
              <SquareButton to="/shop" variant="orange" size="lg" fullWidth testId="home-contact-cta-shop">Shop Mango Boxes</SquareButton>
              <SquareButton to="/wholesale" variant="yellow" size="lg" fullWidth className="-mt-[2px]" testId="home-contact-cta-contact">Wholesale Inquiry</SquareButton>
              <SquareButton to="/contact" variant="cream" size="lg" fullWidth className="-mt-[2px]">Contact Us</SquareButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
